import { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { TransportEquipmentCountStatus } from "__graphql__/types";
import { EquipmentCard } from "flows/Activities/components/TransportEquipment/EquipmentCard";
import {
  useGetActiveEquipmentTypesForSupplierQuery,
  useGetTransportEquipmentCountsQuery,
} from "flows/Activities/queries/deliveryCheckin/transportEquipments.generated";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import {
  DOUBLE_COUNTER_EQUIPMENT_NAMES_DE,
  DOUBLE_COUNTER_EQUIPMENT_NAMES_NL,
} from "flows/Activities/utils/constants";
import { useCountryCode } from "shared/hooks/useCountryCode";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Spinner } from "ui/Spinner/Spinner";
import { TitleM } from "ui/Typography/Typography";

type CountsState = {
  [equipmentTypeId: string]: { returned: number; received: number };
};

export type EquipmentTypeInfo = { id: string; name: string; hasReceivedCounter: boolean };

export type EquipmentCountInfo = {
  lineItems: { equipmentTypeId: string; returned: number; received: number }[];
  equipmentTypes: EquipmentTypeInfo[];
  transportEquipmentCountId?: string;
};

type EquipmentListProps = {
  onCountsChanged: (info: EquipmentCountInfo) => void;
};

export function EquipmentList({ onCountsChanged }: EquipmentListProps) {
  const intl = useIntl();
  const countryCode = useCountryCode();
  const { showToastUI } = useCustomToast();

  const { externalSupplierId } = useDeliveryStore(
    useShallow((state) => ({
      externalSupplierId: state.externalSupplierId,
    })),
  );

  const [countsState, setCountsState] = useState<CountsState>({});
  const [transportEquipmentCountId, setTransportEquipmentCountId] = useState<string | undefined>(
    undefined,
  );

  const doubleCounterEquipmentNames =
    countryCode === "de" ? DOUBLE_COUNTER_EQUIPMENT_NAMES_DE : DOUBLE_COUNTER_EQUIPMENT_NAMES_NL;

  const { data: activeEquipmentTypes, loading: isLoadingActiveEquipmentTypes } =
    useGetActiveEquipmentTypesForSupplierQuery({
      variables: {
        externalSupplierId: externalSupplierId!,
      },
      onError: () => {
        showToastUI({
          title: "components.activities.transport-equipment.count-page.get-equipment.error.title",
        });
      },
      fetchPolicy: "network-only",
      skip: !externalSupplierId,
    });

  const {
    data: equipmentCounts,
    loading: isLoadingEquipmentCounts,
    error: equipmentCountsError,
  } = useGetTransportEquipmentCountsQuery({
    variables: {
      externalSupplierId: externalSupplierId!,
      status: TransportEquipmentCountStatus.pending,
    },
    onError: () => {
      showToastUI({
        title: "components.activities.transport-equipment.count-page.get-counts.error.title",
      });
    },
    fetchPolicy: "network-only",
    skip: !externalSupplierId,
  });

  const hasInitializedRef = useRef(false);
  // State, not a ref - batches with `setCountsState`.
  const [isSeeded, setIsSeeded] = useState(false);

  useEffect(() => {
    // Seed the counts ONCE, after both queries have settled. Re-seeding on every
    // query emission (e.g. a refetch or remount) silently overwrites values the
    // worker has already entered — notably `received`, which the precount step
    // always stores as 0 — resetting them back to 0 with no error. That caused
    // received RCs/TKTs to be recorded as 0 despite goods being received.
    if (hasInitializedRef.current) return;
    if (isLoadingActiveEquipmentTypes || isLoadingEquipmentCounts) return;
    if (!activeEquipmentTypes) return;
    // An error leaves `loading` false with no data; the fallbacks below would read 0.
    if (equipmentCountsError) return;

    const existingCount = equipmentCounts?.getTransportEquipmentCounts.transportEquipmentCounts[0];
    setTransportEquipmentCountId(existingCount?.id);

    const initialCounts: CountsState = {};
    activeEquipmentTypes.getActiveEquipmentTypesForSupplier.equipmentTypes.forEach((item) => {
      const lineItem = existingCount?.lineItems.find((li) => li.equipmentTypeId === item.id);
      initialCounts[item.id] = {
        returned: lineItem?.returned ?? 0,
        received: lineItem?.received ?? 0,
      };
    });
    setCountsState(initialCounts);
    setIsSeeded(true);
    hasInitializedRef.current = true;
  }, [
    activeEquipmentTypes,
    equipmentCounts,
    equipmentCountsError,
    isLoadingActiveEquipmentTypes,
    isLoadingEquipmentCounts,
  ]);

  useEffect(() => {
    if (!activeEquipmentTypes) return;

    const { equipmentTypes } = activeEquipmentTypes.getActiveEquipmentTypesForSupplier;

    if (!isSeeded) return;

    const lineItems = equipmentTypes.map((item) => ({
      equipmentTypeId: item.id,
      returned: countsState[item.id]?.returned ?? 0,
      received: countsState[item.id]?.received ?? 0,
    }));

    onCountsChanged({
      lineItems,
      equipmentTypes: equipmentTypes.map((item) => ({
        id: item.id,
        name: item.name,
        hasReceivedCounter: doubleCounterEquipmentNames.includes(item.name),
      })),
      transportEquipmentCountId,
    });
  }, [
    isSeeded,
    countsState,
    transportEquipmentCountId,
    activeEquipmentTypes,
    doubleCounterEquipmentNames,
    onCountsChanged,
  ]);

  const updateCount = (equipmentTypeId: string, field: "returned" | "received", value: number) => {
    setCountsState((prev) => ({
      ...prev,
      [equipmentTypeId]: {
        ...prev[equipmentTypeId],
        [field]: value,
      },
    }));
  };

  const equipment =
    activeEquipmentTypes?.getActiveEquipmentTypesForSupplier.equipmentTypes.map((item) => {
      const existingCount =
        equipmentCounts?.getTransportEquipmentCounts.transportEquipmentCounts.find((countItem) =>
          countItem.lineItems.some((lineItem) => lineItem.equipmentTypeId === item.id),
        );
      const lineItem = existingCount?.lineItems.find(
        (countLineItem) => countLineItem.equipmentTypeId === item.id,
      );
      const counters = [
        {
          label: intl.formatMessage({
            id: "components.activities.transport-equipment.count-page.count.returned",
          }),
          count: countsState[item.id]?.returned ?? lineItem?.returned ?? 0,
          updateHandler: (count: number) => updateCount(item.id, "returned", count),
        },
      ];
      // Received goes first, matching the TRT slip (Lieferung left, Rückgabe right).
      if (doubleCounterEquipmentNames.includes(item.name)) {
        counters.unshift({
          label: intl.formatMessage({
            id: "components.activities.transport-equipment.count-page.count.received",
          }),
          count: countsState[item.id]?.received ?? lineItem?.received ?? 0,
          updateHandler: (count: number) => updateCount(item.id, "received", count),
        });
      }

      return {
        id: item.id,
        name: item.name,
        photoUrl: item.photoUrl,
        depositInEuros: item.depositInEuros,
        counters,
      };
    }) || [];

  return (
    <>
      <TitleM pt="s300" px="s200" pb="s200">
        {intl.formatMessage({
          id: "components.activities.transport-equipment.count-page.count.title",
        })}
      </TitleM>
      <Box bg="white" borderRadius="sm" p="s200">
        {isLoadingActiveEquipmentTypes || isLoadingEquipmentCounts ? (
          <Spinner display="block" margin="0 auto" />
        ) : (
          equipment.map((item) => (
            <EquipmentCard
              key={item.id}
              name={item.name}
              photoUrl={item.photoUrl}
              depositInEuros={item.depositInEuros}
              counters={item.counters}
            />
          ))
        )}
      </Box>
    </>
  );
}
